import { z } from "zod";

export const MAX_SKILL_TERMS = 30;
export const MAX_SKILL_TERM_LENGTH = 80;
export const MAX_SEMANTIC_SEARCH_LENGTH = 80;

export const skillTermSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_SKILL_TERM_LENGTH);

export const skillTermsSchema = z.array(skillTermSchema).max(MAX_SKILL_TERMS);

export const optionalSkillTermsSchema = skillTermsSchema.optional();

export const optionalSemanticSearchSchema = z
  .string()
  .max(MAX_SEMANTIC_SEARCH_LENGTH)
  .optional();

/**
 * Bounds untrusted URL/import values before they can reach a paid semantic
 * endpoint. UI forms still validate and explain the same limits to the user.
 */
export const boundSkillTerms = (skills: readonly string[]): string[] =>
  skills
    .map((skill) => skill.trim())
    .filter((skill) => skill.length > 0)
    .slice(0, MAX_SKILL_TERMS)
    .map((skill) => skill.slice(0, MAX_SKILL_TERM_LENGTH));

export const boundUnknownSkillTerms = (skills: unknown): string[] =>
  Array.isArray(skills)
    ? boundSkillTerms(
        skills.filter((skill): skill is string => typeof skill === "string"),
      )
    : [];

export const boundSkillsCsv = (
  skills: string | undefined,
): string | undefined => {
  if (!skills) return undefined;
  const bounded = boundSkillTerms(skills.split(","));
  return bounded.length > 0 ? bounded.join(",") : undefined;
};

/**
 * I termini che il freelance scrive nel campo competenze: separati da `,` o
 * `;`, senza spazi in testa e in coda, in minuscolo, senza vuoti. È la stessa
 * normalizzazione di `normalizeTags` in `TagInput`, che non si tocca (P-DL3):
 * il campo del modale deve trattare quello che si scrive come oggi.
 *
 * Non piega gli apostrofi e non toglie i doppioni. Gli apostrofi li piega
 * `skillKey`, che serve a confrontare con le suggerite e con il server, non a
 * riscrivere quello che il freelance ha digitato; i doppioni li scarta chi
 * aggiunge, contro le skill che ci sono già (`addTyped` della bozza).
 */
export const parseSkillInput = (raw: string): string[] =>
  raw
    .split(/[,;]/)
    .map((term) => term.trim().toLowerCase())
    .filter((term) => term !== "");

/**
 * La chiave con cui il server riconosce una skill: la stessa piegatura di
 * `normalizeSkillName` del BE (`utils/profileSkills.ts:22-27`), cioè apostrofi
 * tipografici `‘` e `’` piegati in `'`, spazi in testa e in coda tolti,
 * minuscolo.
 *
 * Serve a ogni confronto d'insieme fra quello che ha il FE e quello che ha il
 * server (P-DL25): `/me` nasconde le suggerite già dichiarate confrontando i
 * nomi normalizzati, e il `PATCH` salva `Node’s` come `node's`. Un confronto
 * sul nome grezzo vedrebbe due skill diverse dove il server ne vede una, e una
 * skill con l'apostrofo tipografico resterebbe «nuova» per sempre.
 *
 * Non tocca gli spazi interni né gli altri caratteri: il server non lo fa, e
 * una chiave più aggressiva della sua unirebbe skill che lui tiene distinte.
 */
export const skillKey = (name: string): string =>
  name.replace(/[‘’]/g, "'").trim().toLowerCase();

/** Le chiavi di una lista di skill, per i confronti d'insieme (P-DL25). */
export const skillKeys = (names: readonly string[]): Set<string> =>
  new Set(names.map(skillKey));

/** `names` in coda a `list`, senza doppioni per chiave. `list` resta com'è. */
export const appendByKey = (
  list: readonly string[],
  names: readonly string[],
): string[] => {
  const known = skillKeys(list);
  const next = [...list];
  for (const name of names) {
    const key = skillKey(name);
    if (known.has(key)) continue;
    known.add(key);
    next.push(name);
  }
  return next;
};

/** Lo stesso insieme per il server, cioè per chiave. */
export const sameSkillSet = (
  a: readonly string[],
  b: readonly string[],
): boolean => {
  const left = skillKeys(a);
  const right = skillKeys(b);
  return left.size === right.size && [...left].every((key) => right.has(key));
};

export const boundSemanticSearch = (
  search: string | undefined,
): string | undefined => {
  const bounded = search?.trim().slice(0, MAX_SEMANTIC_SEARCH_LENGTH);
  return bounded || undefined;
};
